import parsePhoneNumber from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import keys from 'lodash/keys';
import negate from 'lodash/negate';
import * as z from 'zod';
import { countries } from '~/consts/countries';
import { Consent, ConsentStatus, SwanConsentStatus, UserDataItem, UserDataType } from '~/types/consent';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { isSwanInstitution } from './institutions';

export type FormData = Record<
  string,
  | string
  | boolean
  | {
      code: string;
      label: string;
      countryCode: string;
      phone: string;
    }
>;

export interface UpdateConsentData {
  consentId: string;
  userData: {
    key: string;
    value: string;
  }[];
  debug?: boolean;
}

export const isConsentAuthorised = (consent: Consent) => consent.status === ConsentStatus.Authorized;

export const isConsentNonAuthorised = (consent: Consent) =>
  [ConsentStatus.Pending, ConsentStatus.Unauthorized].includes(consent.status);

export const isManualConsent = (consent: Consent) => isNil(consent.institution);

export const isConsentDisabled = (consent: Consent) => consent.status === ConsentStatus.Disabled;

export const isConsentWaiting = (consent: Consent) => consent.status === ConsentStatus.Waiting;

export const isNonManualConsent = negate(isManualConsent);

export const isSwanConsent = (consent: Consent) => !!consent.institution && isSwanInstitution(consent.institution);

export const isSwanOnboardingInProgress = (consent: Consent) =>
  isSwanConsent(consent) && isDefined(consent.redirectUrl) && consent.status === ConsentStatus.Pending;

export const isVisibleSwanConsent = (consent: Consent) =>
  isSwanConsent(consent) &&
  (isSwanOnboardingInProgress(consent) ||
    [ConsentStatus.Authorized, ConsentStatus.Disabled, ConsentStatus.Waiting, ConsentStatus.Unauthorized].includes(
      consent.status,
    ));

export const consentHasPendingSync = (consent: Consent) =>
  isConsentAuthorised(consent) && isNil(consent.lastRunAt) && isEmpty(consent.accounts) && isNonManualConsent(consent);

export const isConsentVisibleToUser = (consent: Consent) =>
  consentHasPendingSync(consent) || !isEmpty(consent.accounts) || isVisibleSwanConsent(consent);

export const shouldInitializeUserData = (consent: Consent) =>
  consent.status === ConsentStatus.Pending && isEmpty(consent.userData) && !consent.redirectUrl;

export const swanConsentStatus = (consent: Consent): SwanConsentStatus | null => {
  if (!isSwanConsent(consent)) return null;

  if (isSwanOnboardingInProgress(consent)) {
    assertIsDefined(consent.redirectUrl, 'Onboarding link is not defined');
    return SwanConsentStatus.MISSING_DATA;
  }

  if (isConsentWaiting(consent)) {
    if (isDefined(consent.userInfo?.text)) {
      return SwanConsentStatus.MISSING_DATA;
    } else {
      return SwanConsentStatus.WAITING_APPROVAL;
    }
  }

  if (isConsentNonAuthorised(consent)) {
    return SwanConsentStatus.UNAUTHORISED;
  }

  return SwanConsentStatus.READY;
};

const getCountryLabel = (code: string) => countries.find((el) => el.code === code)?.label || '';

export const consentUserDataToFormData = (userData: UserDataItem[]): FormData =>
  userData.reduce<FormData>((acc, val) => {
    if (val.type === UserDataType.PHONE) {
      const parsedVal = val.value ? parsePhoneNumber(val.value) : undefined;
      acc[val.key] = parsedVal?.isValid()
        ? {
            code: parsedVal.country || '',
            phone: parsedVal.nationalNumber,
            countryCode: `+${parsedVal.countryCallingCode}`,
            label: getCountryLabel(parsedVal.country || ''),
          }
        : {
            code: 'IT',
            label: getCountryLabel('IT'),
            countryCode: '+39',
            phone: '',
          };
    } else if (val.type === UserDataType.CHECKBOX) {
      acc[val.key] = val.value === 'true';
    } else {
      acc[val.key] = val.value;
    }
    return acc;
  }, {});

export const formDataToConsentUserData = (data: FormData): UpdateConsentData['userData'] =>
  keys(data).reduce<UpdateConsentData['userData']>((acc, key) => {
    const val = data[key]!;
    if (isObject(val)) {
      acc.push({
        key,
        value: `${val.countryCode}${val.phone}`,
      });
    } else if (typeof val === 'boolean') {
      acc.push({
        key,
        value: String(val),
      });
    } else {
      acc.push({
        key,
        value: val,
      });
    }
    return acc;
  }, []);

const phoneSchema = (item: UserDataItem) =>
  z.object({
    code: z.string(),
    label: z.string(),
    countryCode: z.string(),
    phone: item.required ? z.string().min(1).regex(/^\d+$/) : z.string().regex(/^\d*$/).optional(),
  });

export const formDataSchema = (userData: UserDataItem[]): z.ZodType<FormData, FormData> => {
  const schema: Record<string, z.ZodTypeAny> = {};

  userData.forEach((item) => {
    switch (item.type) {
      case UserDataType.EMAIL:
        schema[item.key] = item.required ? z.string().email() : z.union([z.literal(''), z.string().email()]);
        break;
      case UserDataType.PHONE:
        schema[item.key] = z.any().superRefine((val, ctx) => {
          const result = phoneSchema(item).safeParse(val);
          if (!result.success) {
            result.error.issues.forEach((error) => {
              ctx.addIssue({ ...error, message: undefined, path: [] });
            });
          }
        });
        break;
      case UserDataType.CHECKBOX:
        if (item.required) {
          schema[item.key] = z.union([z.boolean(), z.undefined()]).refine((val) => val === true, {
            params: { issue: 'required', fieldName: item.name },
          });
        } else {
          schema[item.key] = z.boolean().optional();
        }
        break;
      default:
        if (item.choices && item.choices.length > 0) {
          schema[item.key] = item.required
            ? z.enum(item.choices.map((c) => c.value) as [string, ...string[]])
            : z.enum(item.choices.map((c) => c.value) as [string, ...string[]]).optional();
        } else {
          schema[item.key] = item.required ? z.string().min(1) : z.string().optional();
        }
        break;
    }
  });

  return z.object(schema) as z.ZodType<FormData, FormData>;
};

/**
 * Merge userData from props with userData from consent.
 *
 * @param userData - userData from consent
 * @param userDataFromProps - userData from props
 * @returns merged userData with userData from props
 */
export const mergeUserData = (userData: UserDataItem[], userDataFromProps?: Record<string, string>) => {
  if (!userDataFromProps) return userData;
  return userData.map((item) => ({
    ...item,
    value: userDataFromProps[item.key] || item.value,
  }));
};
